// PWA Install Banner Component
// Shows a persistent banner at the top of the page to install the app

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Share, Plus } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

export function InstallBanner() {
  const { t } = useTranslation();
  const {
    canInstall,
    isInstalled,
    isIOS,
    isStandalone,
    promptInstall,
    dismissPrompt,
    shouldShowPrompt,
    incrementVisitCount,
  } = useInstallPrompt();

  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Increment visit count on mount
  useEffect(() => {
    incrementVisitCount();
  }, [incrementVisitCount]);

  // Check if banner should be shown
  useEffect(() => {
    if (shouldShowPrompt()) {
      // Small delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowPrompt]);

  // Handle install
  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    setIsInstalling(true);
    const result = await promptInstall();
    setIsInstalling(false);

    if (result === 'accepted') {
      setIsVisible(false);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    setIsVisible(false);
    dismissPrompt();
  };

  // Don't show if already installed or dismissed
  if (isInstalled || isStandalone || !isVisible) {
    return null;
  }

  // Don't show if can't install and not iOS
  if (!canInstall && !isIOS) {
    return null;
  }

  // iOS instructions popup
  if (showIOSInstructions) {
    return (
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 text-white">
        <div className="px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium text-sm mb-2">
                {t('install_banner.ios_title')}
              </p>
              <ol className="text-xs space-y-2">
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-[10px] font-bold flex-shrink-0">1</span>
                  <span>{t('install_banner.ios_step_1_before')}</span>
                  <span className="inline-flex items-center gap-1 bg-white/20 px-1.5 py-0.5 rounded font-medium">
                    <Share className="w-3 h-3" /> {t('install_banner.ios_step_1_button')}
                  </span>
                  <span>{t('install_banner.ios_step_1_after')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-[10px] font-bold flex-shrink-0">2</span>
                  <span>{t('install_banner.ios_step_2_before')}</span>
                  <span className="inline-flex items-center gap-1 bg-white/20 px-1.5 py-0.5 rounded font-medium">
                    <Plus className="w-3 h-3" /> {t('install_banner.ios_step_2_button')}
                  </span>
                </li>
              </ol>
            </div>
            <button
              onClick={() => {
                setShowIOSInstructions(false);
                handleDismiss();
              }}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 text-white">
      <div className="px-4 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:flex p-1.5 bg-white/20 rounded-lg flex-shrink-0">
            <Download className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">
              {t('install_banner.title')}
            </p>
            <p className="text-xs text-cyan-100 hidden sm:block">
              {t('install_banner.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="px-2 py-1 text-xs text-cyan-100 hover:text-white transition-colors hidden sm:block"
          >
            {t('install_banner.later')}
          </button>
          <button
            onClick={handleInstall}
            disabled={isInstalling}
            className="px-3 py-1.5 text-sm font-medium bg-white text-cyan-600 rounded-lg hover:bg-cyan-50 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            {isInstalling ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-cyan-300 border-t-cyan-600 rounded-full animate-spin" />
                <span className="hidden sm:inline">{t('install_banner.installing')}</span>
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5" />
                <span>{t('install_banner.install')}</span>
              </>
            )}
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/10 rounded transition-colors sm:hidden"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallBanner;
